import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { environment } from 'src/environments/environment';
import { Price } from './../../models/Price';
import { Recharge } from './../../models/Recharge';
import { PaginatedResult } from './../model/paginated-result';
import { UserCreditInfo } from 'src/app/models/UserCreditInfo';
import {RechargeFilter} from '../../models/RechargeFilter';
import { BonusQuote } from 'src/app/models/Wallet';
import { OWN_ERROR_TOAST } from '../interceptors/http-error.interceptor';
import { WalletTransfer } from './wallet-transfer.service';

/** Corpo do `POST /recharges` na recarga por valor. Ver `buyByValue`. */
export interface RechargeByValuePayload {
  /** Decimal em string ("200.00"). Ver `microsToDecimalString`. */
  total: string;
  payment_type: string;
  /** Opcional: a carteira é única, mas o backend ainda aceita o rótulo. */
  target?: string;
}

import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RechargesService {

  constructor(private http: HttpClient) { }

  listPaginated(page: number, perPage: number, filter?: RechargeFilter): Observable<PaginatedResult<Recharge[]>> {
    let url = `${environment.apiUrl}/recharges?page=${page}&per_page=${perPage}`;

    if (filter?.text) {
      url += `&text=${encodeURIComponent(filter.text)}`;
    }

    if (filter?.startDate) {
      url += `&start_date=${filter.startDate}`;
    }
    if (filter?.endDate) {
      url += `&end_date=${filter.endDate}`;
    }

    if (filter?.order) {
      url += `&order=${filter.order}`;
    }

    if (filter?.author) {
      url += `&author=${encodeURIComponent(filter.author)}`;
    }

    const orderDirection = filter?.orderDirection ? 'DESC' : 'ASC';
    url += `&direction=${orderDirection}`;

    return this.http.get<PaginatedResult<Recharge[]>>(url).pipe(take(1));
  }

  listall() {
    return this.http.get<object[]>(`${environment.apiUrl}/billing-months`).pipe(take(1));
  }

  price(): Observable<Price[]> {
    return this.http.get<Price[]>(`${environment.apiUrl}/recharges/price`).pipe(take(1));
  }

  buy(recharge: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/recharges`, recharge).pipe(take(1));
  }

  /**
   * Cotação do bônus da recarga por valor (CU-86akn0zp6).
   *
   * `total` vai como decimal em string porque é dinheiro: number deixaria o
   * JavaScript escolher a representação e `200.10` já não é exato em binário.
   * Quem chama converte uma vez, com `microsToDecimalString`.
   */
  bonusQuote(total: string): Observable<BonusQuote> {
    const params = new HttpParams().set('total', total);

    return this.http.get<BonusQuote>(`${environment.apiUrl}/recharges/bonus-quote`, { params });
  }

  /**
   * Compra por VALOR, da coorte da carteira.
   *
   * Mesma rota da compra por quantidade e mesma resposta — o que muda é o corpo:
   * `total` no lugar de `credits`. É de propósito que ela não reaproveita `buy`:
   * o corpo da compra antiga carrega endereço, documento e cartão, e mandar
   * campo a mais numa rota validada por allowlist volta 422.
   */
  buyByValue(payload: RechargeByValuePayload): Observable<any> {
    return this.http.post(`${environment.apiUrl}/recharges`, payload);
  }

  /** Recarga manual do admin. A tela dá os próprios avisos de erro (`OWN_ERROR_TOAST`). */
  add(recharge: any): Observable<Recharge> {
    return this.http.post<Recharge>(`${environment.apiUrl}/recharges/add`, recharge, {
      context: new HttpContext().set(OWN_ERROR_TOAST, true),
    }).pipe(take(1));
  }

  /**
   * Transferência de créditos de SMS/ligação entre contas (`type: 'transfer'`, com
   * `user_id_origin`). Pacote nos dois lados: 200 sem corpo. Carteira nos dois lados: 201 com
   * `{ data: transferência }`, como o `POST /admin/wallet/transfers`. A `idempotency_key`
   * (front-contrato-r3 §4) deduplica o reenvio no caminho da carteira; no pacote é ignorada.
   *
   * A tela dá os próprios avisos de erro (`OWN_ERROR_TOAST`): sem resposta é desfecho incerto.
   */
  transferCredits(transfer: any): Observable<{ data: WalletTransfer } | null> {
    return this.http.post<{ data: WalletTransfer } | null>(`${environment.apiUrl}/recharges/add`, transfer, {
      context: new HttpContext().set(OWN_ERROR_TOAST, true),
    });
  }

  confirm(id: string): Observable<Recharge> {
    return this.http.put<Recharge>(`${environment.apiUrl}/recharges/${id}/confirm`, {}).pipe(take(1));
  }

  delete(id: string, reason: string): Observable<any> {
    let url = `${environment.apiUrl}/recharges/${id}`;
    if (reason && reason !== '') {
      url += `?reason=${reason}`;
    }
    return this.http.delete(url).pipe(take(1));
  }

  transfer(id: string, reason: string, to_user: string): Observable<any> {
    return this.http.post(`${environment.apiUrl}/recharges/${id}/transfer`, { to_user, reason }).pipe(take(1));
  }

  billingMonths(): Observable<object[]> {
    return this.http.get<object[]>(`${environment.apiUrl}/billing-months`).pipe(take(1));
  }

  totalBilling(startDate?: string, endDate?: string, profileId?: string): Observable<any> {
    let filter = '?';

    if (startDate) {
      filter += `&start=${startDate}`;
    }

    if (endDate) {
      filter += `&end=${endDate}`;
    }

    if (profileId) {
      filter += `&profile_id=${profileId}`;
    }

    return this.http.get(`${environment.apiUrl}/total-billing${filter}`).pipe(take(1));
  }

  getPixImage(rechargeId: string): Observable<any> {
    return this.http.get<any>(`${environment.apiUrl}/pix/${rechargeId}`).pipe(take(1));
  }

  getUserCreditInfo(userId: string): Observable<UserCreditInfo> {
    return this.http.get<UserCreditInfo>(`${environment.apiUrl}/user-credits-info?simulate=${userId}`).pipe(take(1));
  }

  download(startDate: string, endDate: string, profileId?: string) {
    let queryString = '?';

    if (startDate){
      queryString += `&start=${startDate}`
    }

    if (endDate) {
      queryString += `&end=${endDate}`
    }

    if (profileId) {
      queryString += `&profile_id=${profileId}`
    }

    this.http.get(`${environment.apiUrl}/credits-info/download${queryString}`, {
      responseType: "blob",
      headers: new HttpHeaders().append("Content-Type", "application/json")
    })
    .pipe(take(1))
    .subscribe(data => {
      const contentType = 'text/csv';
      const blob = new Blob([data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      const fileName = 'smsfunnel_billing_' + (startDate || 'all') + '_' + (endDate || 'all') + '_' + Date.now() + '.csv';
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

}
